<template>
  <div class="custom-table-wrapper">
    <div class="table-content">
      <div v-if="show_header" class="table-header">
        <h4 v-if="show_title">
          {{ title || $t('shared.component.table.overview') }}
        </h4>

        <div class="options flex flex--center">
          <div v-if="show_filter" class="flex flex--center mr-4">
            <slot name="filter"> </slot>
          </div>
          <div class="per-page-slot">
            <slot name="per-page-select">
              <div v-if="show_per_page">
                <label>
                  {{ $t('shared.component.table.show') }}
                  <b-form-select
                    :value="perPage"
                    :options="[5, 10, 25, 50, 100]"
                    @input="changePerPage"
                  />
                  {{ $t('shared.component.table.records') }}
                </label>
              </div>
            </slot>
            <div v-if="options">
              <i-button v-b-toggle.options variant="primary" class="icon--config ml-4" />
              <b-collapse id="options" class="mt-2">
                <checkbox-group v-model="state" @input="check">
                  <template v-for="field in fields">
                    <checkbox v-if="field.checkbox" :key="field.key" :label="field.key">
                      {{ field.label }}
                    </checkbox>
                  </template>
                </checkbox-group>
              </b-collapse>
            </div>
          </div>
        </div>
      </div>
      <div class="table-loading-wrapper">
        <b-table
          show-empty
          :responsive="true"
          hover
          :foot-clone="foot_clone"
          :fields="transformFields"
          :items="filter_items || items"
          :busy="isLoading"
          :per-page="perPage"
          :no-sort-reset="true"
          :sort-by.sync="sort_by"
          :sort-desc.sync="sort_direction"
          :class="['table-fw', ...table_class]"
          :tbody-tr-class="tbody_tr_class"
          :empty-html="getText"
          :no-footer-sorting="no_footer_sorting"
          @sort-changed="bootstrapSortBy"
          @row-clicked="clickRow"
        >
          <template #head(billingTerms)="headCell">
            <poptip width="380" trigger="hover" word-wrap :transfer="true">
              <div slot="content" style="text-align: left">
                <span v-html="tooltip_text"> </span>
              </div>
              <span>{{ headCell.label }}</span>
              <icon size="18" type="ios-information-circle-outline" class="ml-2 mb-0" />
            </poptip>
          </template>

          <template #head()="{ label, column }">
            <poptip
              v-if="!!get_tooltip(column)"
              :width="get_tooltip_width(column)"
              trigger="hover"
              word-wrap
              :transfer="true"
            >
              <div slot="content" style="text-align: left">
                <span v-html="get_tooltip(column)"> </span>
              </div>
              <span>{{ label }}
                <icon size="18" type="ios-information-circle-outline" class="ml-1 mb-0" />
              </span>
            </poptip>
          </template>

          <template #head(peer)="headCell">
            <poptip width="230" trigger="hover" word-wrap :transfer="true">
              <div slot="content" style="text-align: left">
                <span v-html="tooltip_text"> </span>
              </div>
              <span>{{ headCell.label }}</span>
              <icon size="18" type="ios-information-circle-outline" class="ml-2 mb-0" />
            </poptip>
          </template>

          <template #row-details="params">
            <slot name="row-details" v-bind="params"></slot>
          </template>

          <template
            v-for="cell in fields"
            v-slot:[getSlot(cell)]="{ item, value: formatted, unformatted, ...others }"
          >
            <slot
              v-if="cell.key == 'recording'"
              :name="cell.key"
              v-bind="{ item, formatted, unformatted, ...others }"
            >
              <p v-if="formatted" :key="formatted" class="audio" controls>
                <audio-player :src="formatted" :load="download_audio_allowed" />
              </p>
            </slot>
            <slot v-else :name="cell.key" v-bind="{ item, formatted, unformatted, ...others }">
              {{ formatted }}
            </slot>
          </template>

          <template v-for="cell in fields" v-slot:[getSlotFoot(cell)]="">
            {{ getTotal(cell.key, cell) || '' }}
          </template>

          <template v-slot:[getTotalFoot()]="">
            {{ $t('mailer.stats.general.field.total') }}
          </template>
        </b-table>

        <div v-if="isLoading" class="table-loading-overlay">
          <b-spinner variant="primary" />
        </div>
      </div>
    </div>

    <div v-if="!unstable_pagination && totalItems > perPage" class="row align-items-center mt-2">
      <div class="paginate-counter col-md-6">
        {{
          $t('shared.component.table.paginate_info', {
            start: currentItemsIndexStart,
            end: currentItemsIndexEnd,
            all: totalItems
          })
        }}
      </div>
      <div class="col-md-6">
        <b-pagination
          :disabled="isLoading"
          :value="currentPage"
          :per-page="perPage"
          :total-rows="totalItems"
          :limit="7"
          align="right"
          style="margin: 0"
          @input="changePage"
        />
      </div>
    </div>

    <!-- media core stats cann't return over 5000 -->
    <div v-else-if="unstable_pagination" class="row align-items-center mt-2">
      <div class="paginate-counter col-md-6"></div>
      <div class="col-md-6">
        <b-pagination
          :disabled="isLoading"
          :value="currentPage"
          :per-page="perPage"
          align="right"
          style="margin: 0"
          :hide-goto-end-buttons="true"
          :total-rows="totalItems"
          :limit="1"
          @input="changePage"
        />
      </div>
    </div>

    <div class="mt-4">
      <span v-if="isBadgeRendered" class="counter-card">
        {{ $t('mailer.stats.detailed.total') }}: {{ totalItems }}
      </span>
    </div>
  </div>
</template>

<script>
  import {
    CURRENT_PAGE,
    DATA,
    IS_LOADING,
    PER_PAGE,
    SORT_COLUMN,
    SORT_DIRECTION,
    TOTAL_ITEMS,
    IS_FAIL_RESPONSE,
    FAIL_RESPONSE
  } from 'vuex-async-paginate/src/const';
  import { toUserDateWithTime } from '@shared/helpers/date_format';
  import { mapGetters } from 'vuex';
  import { seconds_to_time } from '@shared/helpers/time';
  import AudioPlayer from '@app/plugins/audio-player/AudioPlayer';

  export default {
    name: 'CustomTable',
    components: {
      AudioPlayer
    },
    props: {
      tooltip_text: {
        type: String,
        default: null
      },
      title: {
        type: String,
        default: null
      },

      fields: {
        type: Array,
        default: () => []
      },

      download_audio_allowed: {
        type: Boolean,
        default: false
      },

      options: {
        type: Boolean,
        default: false
      },

      state: {
        type: Array,
        default: () => []
      },

      table_class: {
        type: [Array, String],
        default: ''
      },

      tbody_tr_class: {
        type: Function,
        default: null
      },

      table_store: {
        type: String,
        default: null
      },

      show_per_page: {
        type: Boolean,
        default: true
      },

      show_filter: {
        type: Boolean,
        default: false
      },

      show_title: {
        type: Boolean,
        default: true
      },

      show_header: {
        type: Boolean,
        default: true
      },

      auto_start: {
        type: Boolean,
        default: true
      },
      from_first_page: {
        type: Boolean,
        default: false
      },

      foot_clone: {
        type: Boolean,
        default: false
      },

      no_footer_sorting: {
        type: Boolean,
        default: true
      },

      total_caption: {
        type: String,
        default: 'caption'
      },

      filter_items: {
        type: Array,
        default: undefined
      },

      sort_date: {
        type: Boolean,
        default: false
      },

      need_archived: {
        type: String,
        default: ''
      },

      unstable_pagination: {
        type: Boolean,
        default: false
      },

      select_filter_message: {
        type: String,
        default: ''
      }
    },

    data() {
      return {
        text: '',
        total: 0,
        answered: 0,
        total_duration: 0
      };
    },

    computed: {
      ...mapGetters('core/auth/user', 'currency'),

      transformFields: function () {
        return this.fields.map((item) => {
          const default_date_fields = [
            'created_at',
            'updated_at',
            'lead_date',
            'delayed_until',
            'last_attempt'
          ];

          if (default_date_fields.includes(item.key)) {
            const is_have_formatter = 'formatter' in item;

            if (!is_have_formatter) {
              return {
                ...item,
                formatter: (value) => toUserDateWithTime(value)
              };
            }
          }

          return item;
        });
      },

      getText: function () {
        const currentUrl = window.location.pathname;

        if (this.isLoading) {
          return this.$t('shared.component.table.loading');
        } else if (this.isErrorResponse) {
          const message = this.errorResponse?.response?.data?.message;

          if (message) {
            if (this.$te(`shared.errors["${message}"]`)) {
              return this.$t(`shared.errors["${message}"]`)
            }
            return message;
          } else {
            return this.$t('shared.component.table.error');
          }
        }

        if (this.select_filter_message) {
          return this.select_filter_message;
        }

        if (currentUrl === '/mailer/mailings/new') {
          return this.$t('shared.component.table.empty_db');
        }

        return this.$t('shared.component.table.empty');
      },

      items: function () {
        const data = this.getter(DATA);
        if (this.sort_date) data.sort((a, b) => (a.min_date > b.min_date ? 1 : -1));
        // TODO: delete rates legacy
        if (this.need_archived === 'expiration') {
          const currentDate = new Date();
          return data.filter((i) => Date.parse(i.end) > currentDate);
        } else {
          return data;
        }
      },

      isLoading: function () {
        return this.getter(IS_LOADING);
      },

      errorResponse: function () {
        return this.getter(FAIL_RESPONSE);
      },

      isErrorResponse: function () {
        return this.getter(IS_FAIL_RESPONSE);
      },

      perPage: function () {
        return this.getter(PER_PAGE);
      },

      sortColumn: function () {
        return this.getter(SORT_COLUMN);
      },

      sortDirection: function () {
        return this.getter(SORT_DIRECTION);
      },

      totalItems: function () {
        const total = this.getter(TOTAL_ITEMS);

        if (this.unstable_pagination && total === null) {
          return 10000;
        } else {
          return total;
        }
      },

      currentPage: function () {
        return this.getter(CURRENT_PAGE);
      },

      sort_data: {
        get: function () {
          return {
            sort_by: this.sort_by,
            sort_direction: this.sort_direction
          };
        },

        set: function (val) {
          this.action('changeSort', val);
        }
      },

      sort_by: {
        get: function () {
          return this.sortColumn;
        },

        set: function (val) {
          const params = {
            [this.getKey(SORT_COLUMN)]: val,
            [this.getKey(SORT_DIRECTION)]: this.sort_direction === true ? 'desc' : 'asc'
          };

          this.$emit('change-sort', params);
          this.action('changeSort', params);
        }
      },

      sort_direction: {
        get: function () {
          return this.sortDirection === 'desc';
        },

        set: function (val) {
          const params = {
            [this.getKey(SORT_COLUMN)]: this.sort_by,
            [this.getKey(SORT_DIRECTION)]: val === true ? 'desc' : 'asc'
          };

          this.$emit('change-sort', params);
          this.action('changeSort', params);
        }
      },

      currentItemsIndexStart: function () {
        return this.currentPage * this.perPage - this.perPage + 1;
      },

      currentItemsIndexEnd: function () {
        const end = this.currentPage * this.perPage;
        return end >= this.totalItems ? this.totalItems : end;
      },

      isBadgeRendered: function () {
        if (this.$route) {
          return (
            this.totalItems && this.totalItems < 11 && this.$route.path === '/mailer/stats/detailed'
          );
        }

        return null;
      }
    },

    mounted() {
      this.text = this.$t('prices.common.info.billing_terms_tooltip');
      if (this.from_first_page) {
        return this.action('getAsyncData', { current_page: 1 });
      }
      if (this.auto_start) {
        this.action('getAsyncData');
      }
    },

    methods: {
      get_tooltip(column) {
        return this.fields.find((i) => i.key === column).tooltip;
      },
      get_tooltip_width(column) {
        return this.fields.find((i) => i.key === column)?.tooltipWidth || '230';
      },
      clickRow(row) {
        this.$emit('click_row', row);
      },
      download_file(href) {
        const link = document.createElement('a');
        link.href = href;
        link.target = '_blank';
        link.click();
      },
      getSlot(cell) {
        return `cell(${cell.key})`;
      },

      getMusic(item) {
        return {
          src: item
        };
      },

      getSlotFoot(cell) {
        return `foot(${cell.key})`;
      },

      getTotalFoot() {
        return `foot(${this.total_caption})`;
      },

      bootstrapSortBy: function (params) {
        this.sort_data = {
          [this.getKey(SORT_COLUMN)]: params.sortBy,
          [this.getKey(SORT_DIRECTION)]: params.sortDesc === true ? 'desc' : 'asc'
        };

        this.$emit('change_sort', params);
      },

      getTotal(cellKey, cell) {
        let accumulator = 0;

        if (cellKey === 'answered') {
          for (const item of this.items) {
            accumulator += +item[cellKey];
          }

          return (this.answered = accumulator);
        }

        if (cellKey === 'total') {
          for (const item of this.items) {
            accumulator += +item[cellKey];
          }
          return (this.total = accumulator);
        }

        if (cell.percentage && cellKey === 'asr') {
          if (this.total > 0) {
            accumulator = (this.answered * 100) / this.total;
            return `${accumulator.toFixed(2)}%`;
          }
          return 0 + '%';
        }

        if (cellKey === 'duration') {
          for (const item of this.items) {
            accumulator += +item[cellKey];
          }

          this.total_duration = accumulator;
          return seconds_to_time(accumulator);
        }

        if (cellKey === 'duration_billed') {
          for (const item of this.items) {
            accumulator += +item[cellKey];
          }

          return seconds_to_time(accumulator);
        }

        if (cellKey === 'acd') {
          if (this.answered > 0) {
            accumulator = this.total_duration / this.answered;
          } else {
            accumulator = 0;
          }
          return seconds_to_time(accumulator);
        }

        if (cell.percentage) {
          for (const item of this.items) {
            accumulator += +item[cellKey];
          }
          return `${accumulator}%`;
        }

        if (cell.time) {
          for (const item of this.items) {
            accumulator += +item[cellKey];
          }
          return seconds_to_time(accumulator);
        }

        for (const item of this.items) {
          accumulator += +item[cellKey];
        }

        if (!accumulator && this.items.length > 0) {
          return '0';
        }

        if (accumulator && (cellKey === 'cost' || cellKey === 'op_sum')) {
          accumulator = this.$numeral.currency(accumulator, this.currency);
        }

        return accumulator;
      },

      getKey: function (name) {
        const keys = this.$store.getters[`${this.table_store}/keys`];
        return keys[name];
      },

      action: function (name, params = {}) {
        if (this.table_store) {
          return this.$store.dispatch(`${this.table_store}/${name}`, params);
        }
      },
      getter: function (name) {
        const keys = this.$store.getters[`${this.table_store}/keys`];
        return this.$store.getters[`${this.table_store}/${keys[name]}`];
      },

      changePerPage(perPage) {
        this.$emit('change-per-page', perPage);
        this.action('changePerPage', perPage);
      },

      changePage(page) {
        this.$emit('change-page', page);
        this.action('changePage', page);
      },

      check() {
        this.$emit('check', this.state);
      }
    }
  };
</script>

<style lang="scss">
  .counter-card {
    padding: 10px 15px;
    border: 1px solid $grey-dark;
    border-radius: 3px;
    background: $bg-white;
  }

  .block {
    display: block;
    padding: 20px 10px;
  }
  .w-25 {
    width: 400px;
  }
  .audio {
    .range {
      width: 150px;
      input {
        height: 2px;
        top: 4px;
        cursor: pointer;
      }
    }
    .counters {
      max-width: 100px;
    }
  }
  .ivu-tooltip-rel {
    cursor: default;
  }
  // .ivu-poptip {
  //   &.ivu-icon-ios-help-circle-outline:before {
  //     margin-bottom: 0px;
  //   }
  // }
</style>
